import { 
  SignatureGridData,
  GridCell,
  createCellId,
} from './grid/types';
import { 
  SignatureBlock,
  createImageBlock,
  createTextBlock,
  createContactRowBlock,
  createDividerBlock,
  createSpacerBlock,
  createSocialIconsBlock,
} from './blocks/types';

export interface SignatureTemplate {
  id: string;
  name: string;
  description: string;
  thumbnail: string;
  data: SignatureGridData;
}

// Helper to create a cell with content
const cellWithContent = (content: SignatureBlock, depth: number = 0): GridCell => ({
  id: createCellId(),
  type: 'cell',
  content,
  children: null,
  splitDirection: null,
  widths: null,
  depth,
});

// Helper to create a split cell
const splitCell = (
  direction: 'horizontal' | 'vertical',
  widths: string[],
  children: GridCell[],
  depth: number = 0
): GridCell => ({
  id: createCellId(),
  type: 'cell',
  content: null,
  children,
  splitDirection: direction,
  widths,
  depth,
});

// Helper to create empty cell
const emptyCell = (depth: number = 0): GridCell => ({
  id: createCellId(),
  type: 'cell',
  content: null,
  children: null,
  splitDirection: null,
  widths: null,
  depth,
});

export const signatureTemplates: SignatureTemplate[] = [
  {
    id: 'professional-two-column',
    name: 'Professional Two-Column',
    description: 'Logo on left with contact details on right',
    thumbnail: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    data: {
      version: 2,
      rootCells: [
        splitCell('horizontal', ['30%', '70%'], [
          // Left column - Logo
          splitCell('vertical', ['auto', 'auto'], [
            cellWithContent(createImageBlock({ width: 100, src: '' }), 2),
          ], 1),
          // Right column - Contact details stacked
          splitCell('vertical', ['auto', 'auto', 'auto', 'auto', 'auto'], [
            cellWithContent(createTextBlock({
              content: '{{user_full_name}}',
              fontSize: 16,
              fontWeight: 'bold',
              color: '#1a1a1a',
              fontFamily: 'Arial, sans-serif',
            }), 2),
            cellWithContent(createTextBlock({
content: '{{company_position}}',
              fontSize: 13,
              fontWeight: 'normal',
              color: '#666666',
              fontFamily: 'Arial, sans-serif',
            }), 2),
            cellWithContent(createSpacerBlock({ height: 8 }), 2),
            cellWithContent(createContactRowBlock({
              icon: 'email',
              label: '{{user_email}}',
              iconColor: '#666666',
              iconSize: 14,
              fontSize: 12,
              textColor: '#333333',
              linkType: 'email',
            }), 2),
            cellWithContent(createContactRowBlock({
              icon: 'phone',
              label: '{{user_phone}}',
              iconColor: '#666666',
              iconSize: 14,
              fontSize: 12,
              textColor: '#333333',
              linkType: 'phone',
            }), 2),
          ], 1),
        ], 0),
      ],
      globalStyles: {
        fontFamily: 'Arial, sans-serif',
        backgroundColor: '#ffffff',
        padding: 0,
      },
    },
  },
  {
    id: 'simple-stacked',
    name: 'Simple Stacked',
    description: 'Clean vertical layout with centered content',
    thumbnail: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    data: {
      version: 2,
      rootCells: [
        cellWithContent(createTextBlock({
          content: '{{user_full_name}}',
          fontSize: 16,
          fontWeight: 'bold',
          color: '#1a1a1a',
          fontFamily: 'Arial, sans-serif',
          textAlign: 'left',
        }), 0),
        cellWithContent(createTextBlock({
          content: '{{company_position}} | {{company_name}}',
          fontSize: 13,
          fontWeight: 'normal',
          color: '#666666',
          fontFamily: 'Arial, sans-serif',
        }), 0),
        cellWithContent(createSpacerBlock({ height: 8 }), 0),
        cellWithContent(createDividerBlock({
          orientation: 'horizontal',
          thickness: 1,
          color: '#e0e0e0',
          length: 100,
        }), 0),
        cellWithContent(createSpacerBlock({ height: 8 }), 0),
        cellWithContent(createContactRowBlock({
          icon: 'email',
          label: '{{user_email}}',
          iconColor: '#666666',
          iconSize: 14,
          fontSize: 12,
          textColor: '#333333',
          linkType: 'email',
        }), 0),
        cellWithContent(createContactRowBlock({
          icon: 'phone',
          label: '{{user_phone}}',
          iconColor: '#666666',
          iconSize: 14,
          fontSize: 12,
          textColor: '#333333',
          linkType: 'phone',
        }), 0),
      ],
      globalStyles: {
        fontFamily: 'Arial, sans-serif',
        backgroundColor: '#ffffff',
        padding: 0,
      },
    },
  },
  {
    id: 'modern-minimal',
    name: 'Modern Minimal',
    description: 'Sleek single-line style',
    thumbnail: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    data: {
      version: 2,
      rootCells: [
        cellWithContent(createTextBlock({
          content: '{{user_full_name}}',
          fontSize: 15,
          fontWeight: 'bold',
          color: '#1a1a1a',
          fontFamily: 'Arial, sans-serif',
        }), 0),
        cellWithContent(createTextBlock({
content: '{{company_position}}',
              fontSize: 12,
          fontWeight: 'normal',
          color: '#888888',
          fontFamily: 'Arial, sans-serif',
        }), 0),
        cellWithContent(createSpacerBlock({ height: 6 }), 0),
        cellWithContent(createTextBlock({
          content: '{{user_email}} • {{user_phone}}',
          fontSize: 12,
          fontWeight: 'normal',
          color: '#666666',
          fontFamily: 'Arial, sans-serif',
        }), 0),
      ],
      globalStyles: {
        fontFamily: 'Arial, sans-serif',
        backgroundColor: '#ffffff',
        padding: 0,
      },
    },
  },
  {
    id: 'corporate-full',
    name: 'Corporate Full',
    description: 'Complete business signature with logo and social',
    thumbnail: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    data: {
      version: 2,
      rootCells: [
        splitCell('horizontal', ['25%', '75%'], [
          // Left column - Logo
          cellWithContent(createImageBlock({ width: 120, src: '' }), 1),
          // Right column - Details
          splitCell('vertical', ['auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto'], [
            cellWithContent(createTextBlock({
              content: '{{user_full_name}}',
              fontSize: 16,
              fontWeight: 'bold',
              color: '#1a1a1a',
              fontFamily: 'Arial, sans-serif',
            }), 2),
            cellWithContent(createTextBlock({
content: '{{company_position}}',
              fontSize: 13,
              fontWeight: 'normal',
              color: '#666666',
              fontFamily: 'Arial, sans-serif',
            }), 2),
            cellWithContent(createTextBlock({
              content: '{{company_name}}',
              fontSize: 13,
              fontWeight: 'bold',
              color: '#444444',
              fontFamily: 'Arial, sans-serif',
            }), 2),
            cellWithContent(createSpacerBlock({ height: 8 }), 2),
            cellWithContent(createContactRowBlock({
              icon: 'email',
              label: '{{user_email}}',
              iconColor: '#666666',
              iconSize: 14,
              fontSize: 12,
              textColor: '#333333',
              linkType: 'email',
            }), 2),
            cellWithContent(createContactRowBlock({
              icon: 'phone',
              label: '{{user_phone}}',
              iconColor: '#666666',
              iconSize: 14,
              fontSize: 12,
              textColor: '#333333',
              linkType: 'phone',
            }), 2),
            cellWithContent(createContactRowBlock({
              icon: 'website',
              label: '{{company_website}}',
              iconColor: '#666666',
              iconSize: 14,
              fontSize: 12,
              textColor: '#333333',
              linkType: 'url',
            }), 2),
            cellWithContent(createSocialIconsBlock({
              iconSize: 20,
              iconStyle: 'color',
              spacing: 8,
              icons: [
                { platform: 'linkedin', url: 'https://linkedin.com/' },
                { platform: 'facebook', url: 'https://facebook.com/' },
                { platform: 'instagram', url: 'https://instagram.com/' },
              ],
            }), 2),
          ], 1),
        ], 0),
      ],
      globalStyles: {
        fontFamily: 'Arial, sans-serif',
        backgroundColor: '#ffffff',
        padding: 0,
      },
    },
  },
  {
    id: 'blank',
    name: 'Start from Scratch',
    description: 'Empty canvas for custom design',
    thumbnail: 'linear-gradient(135deg, #e0e0e0 0%, #f5f5f5 100%)',
    data: {
      version: 2,
      rootCells: [emptyCell(0)],
      globalStyles: {
        fontFamily: 'Arial, sans-serif',
        backgroundColor: '#ffffff',
        padding: 0,
      },
    },
  },
];

export const getTemplateById = (id: string): SignatureTemplate | undefined => {
  return signatureTemplates.find(t => t.id === id);
};
